<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Check, ChevronDown, Globe } from 'lucide-vue-next';
import { SUPPORTED_LOCALES, LOCALE_LABELS, setLocale, type Locale } from '../i18n';

// Globe dropdown for switching locale. `compact` renders the 28px version
// for the utility bar; the default 40px version sits in the mobile drawer.
// The menu is teleported to <body> and positioned from the trigger's rect,
// because every host (the folding utility band, the scrolling drawer) clips
// overflow and would cut an in-flow dropdown off. `placement="up"` opens it
// above the trigger. Click-outside, Escape, scroll and resize dismiss.
const props = withDefaults(defineProps<{ compact?: boolean; placement?: 'down' | 'up' }>(), {
  compact: false,
  placement: 'down',
});

const { locale, t } = useI18n();
const open = ref(false);
const root = ref<HTMLElement | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

const MENU_WIDTH = 160;
const GAP = 6;

function place() {
  const r = trigger.value?.getBoundingClientRect();
  if (!r) return;
  const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
  menuStyle.value =
    props.placement === 'up'
      ? { left: `${Math.max(8, r.left)}px`, bottom: `${window.innerHeight - r.top + GAP}px`, width: `${MENU_WIDTH}px` }
      : { left: `${left}px`, top: `${r.bottom + GAP}px`, width: `${MENU_WIDTH}px` };
}

function toggle() {
  if (open.value) {
    close();
    return;
  }
  place();
  open.value = true;
  document.addEventListener('click', onOutside);
  document.addEventListener('keydown', onKey);
  window.addEventListener('scroll', close, true);
  window.addEventListener('resize', close);
}

function close() {
  open.value = false;
  document.removeEventListener('click', onOutside);
  document.removeEventListener('keydown', onKey);
  window.removeEventListener('scroll', close, true);
  window.removeEventListener('resize', close);
}

function onOutside(e: MouseEvent) {
  const target = e.target as Node;
  if (root.value?.contains(target)) return;
  if ((target as Element).closest?.('[data-language-menu]')) return;
  close();
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') close();
}

function choose(next: Locale) {
  setLocale(next);
  close();
}

onBeforeUnmount(close);
</script>

<template>
  <div ref="root" class="relative">
    <button
      ref="trigger"
      type="button"
      :aria-expanded="open"
      aria-haspopup="menu"
      :aria-label="t('common.changeLanguage')"
      class="flex items-center gap-1.5 rounded-md font-medium uppercase text-text-dim transition-colors hover:bg-card hover:text-text"
      :class="compact ? 'h-7 px-1.5 text-[12px]' : 'h-10 px-2.5 text-[13px]'"
      @click.stop="toggle"
    >
      <Globe :class="compact ? 'size-3.5' : 'size-[18px]'" :stroke-width="1.75" />
      <span>{{ locale }}</span>
      <ChevronDown class="size-3 text-text-mute transition-transform" :class="open ? 'rotate-180' : ''" :stroke-width="2" />
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        role="menu"
        data-language-menu
        class="fixed z-[60] rounded-lg border border-border bg-surface p-1.5 shadow-lift"
        :style="menuStyle"
      >
        <button
          v-for="code in SUPPORTED_LOCALES"
          :key="code"
          type="button"
          role="menuitemradio"
          :aria-checked="locale === code"
          class="flex h-9 w-full items-center justify-between rounded-md px-3 text-[14px] text-text hover:bg-card"
          @click="choose(code)"
        >
          <span>{{ LOCALE_LABELS[code] }}</span>
          <Check v-if="locale === code" class="size-4 text-primary" :stroke-width="2.5" />
        </button>
      </div>
    </Teleport>
  </div>
</template>
